import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { ensureSyntaxTree, foldAll } from "@codemirror/language";
import { oneDark } from "@codemirror/theme-one-dark";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { getAuditBlob, listAudits, type AuditRow } from "@/lib/api";
import { cn, formatTimestamp } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./audit.css";

const PAGE_SIZE = 50;
const ACTIONS: [string, string][] = [["", "All actions"], ["update_user_data", "Update User Data"], ["send_mail", "Send Mail"], ["grant_items", "Grant Items"], ["ban", "Ban"], ["revoke_bans", "Revoke Bans"], ["flag_rescanned", "Rescan Flags"]];
const ACTION_TONE: Record<string, string> = { ban: "pw-badge-danger", revoke_bans: "au-badge-ok" };
const playerHref = (id: string) => `/accounts?player=${encodeURIComponent(id)}`;

const shortTime = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export function AuditWorkspace() {
  const readOnly = usePreviewReadOnly();
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<number | null>(null);
  const playerFilter = params.get("playfabId") ?? "";
  const adminFilter = params.get("admin") ?? "";
  const actionFilter = params.get("action") ?? "";
  const rawOffset = Number(params.get("offset") ?? 0);
  const offset = Number.isSafeInteger(rawOffset) && rawOffset > 0 ? rawOffset : 0;
  const query = useQuery({
    queryKey: ["audit", playerFilter, adminFilter, actionFilter, offset],
    queryFn: () => listAudits({ playfabId: playerFilter || undefined, admin: adminFilter || undefined, action: actionFilter || undefined, limit: PAGE_SIZE, offset }),
  });
  function setParam(k: string, v: string | undefined) {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    next.delete("offset");
    setParams(next, { replace: true });
  }
  function setOffset(o: number) {
    const next = new URLSearchParams(params);
    if (o <= 0) next.delete("offset"); else next.set("offset", String(o));
    setParams(next, { replace: true });
  }
  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];
  const filtered = !!(playerFilter || adminFilter || actionFilter);
  const open = openId === null ? null : rows.find(r => r.id === openId) ?? null;

  return <WorkspaceShell crumbs={[{ label: "Audit log" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page au-page">
      <div className="ws-head">
        <h1>Audit log</h1>
        {query.data && <span className="pw-badge">{total.toLocaleString()}</span>}
      </div>
      <div className="ws-form au-filters">
        <div className="ws-row">
          <label className="pw-toolbar-search au-search"><Search size={14} aria-hidden="true" /><input aria-label="Filter by UID" placeholder="Player UID" value={playerFilter} onChange={e => setParam("playfabId", e.target.value)} /></label>
          <label className="pw-toolbar-search au-search"><Search size={14} aria-hidden="true" /><input aria-label="Filter by admin Discord ID" placeholder="Admin Discord ID" value={adminFilter} onChange={e => setParam("admin", e.target.value)} /></label>
          <select aria-label="Action" value={actionFilter} onChange={e => setParam("action", e.target.value)}>{ACTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
          {filtered && <button className="pw-button pw-button-plain" onClick={() => setParams({}, { replace: true })}><X size={13} />Clear</button>}
        </div>
      </div>
      {query.error && <p className="ws-alert" role="alert">{String(query.error)}</p>}
      <section className="ws-card is-flush">
        <div className="ws-table" role="table" aria-label="Audit entries">
          <div className="ws-table-row ws-table-head au-row" role="row">{["When", "Admin", "Action", "Target", "Summary"].map(label => <span role="columnheader" key={label}>{label}</span>)}</div>
          {query.isLoading ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading audit log…</div>
            : rows.length === 0 ? <p className="ws-empty">No audit entries.</p>
            : rows.map(row => {
              const details = !!(row.blob_key || row.metadata);
              return <div key={row.id} role="row" className={cn("ws-table-row au-row", details && "is-clickable")} tabIndex={details ? 0 : undefined}
                onClick={e => { if (details && !(e.target as HTMLElement).closest("a")) setOpenId(row.id); }} onKeyDown={e => { if (details && (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); setOpenId(row.id); } }}>
                <span role="cell" className="au-time" title={formatTimestamp(row.ts)}>{shortTime.format(row.ts)}</span>
                <span role="cell" className="au-admin" title={row.admin_discord}><strong>{row.admin_name}</strong></span>
                <span role="cell"><span className={cn("pw-badge", ACTION_TONE[row.action])}>{row.action}</span></span>
                <span role="cell" className="au-target">{row.target_playfab ? <Link to={playerHref(row.target_playfab)} title={row.target_playfab}>{row.target_label ?? row.target_playfab.slice(0, 8) + "…"}</Link> : "—"}</span>
                <span role="cell" className="au-summary" title={row.summary}>{row.summary}{details && <ChevronRight size={14} aria-hidden="true" />}</span>
              </div>;
            })}
        </div>
      </section>
      {total > PAGE_SIZE && <div className="au-pager">
        <span className="ws-muted">{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
        <div className="ws-row">
          <button className="pw-icon-button au-page-button" aria-label="Previous page" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}><ChevronLeft size={15} /></button>
          <button className="pw-icon-button au-page-button" aria-label="Next page" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}><ChevronRight size={15} /></button>
        </div>
      </div>}
    </div></div>
    {open && <AuditDialog row={open} onClose={() => setOpenId(null)} />}
  </WorkspaceShell>;
}

function pretty(s: string | undefined): string {
  if (!s) return "";
  try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s; }
}

function AuditDialog({ row, onClose }: { row: AuditRow; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => { if (dialog.current && !dialog.current.open) dialog.current.showModal(); }, []);
  const blobQuery = useQuery({ queryKey: ["audit-blob", row.id], queryFn: () => (row.blob_key ? getAuditBlob(row.id) : null), enabled: !!row.blob_key });
  const before = blobQuery.data?.before ?? {};
  const after = blobQuery.data?.after ?? {};
  const allKeys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
  let metadata: unknown = null;
  if (row.metadata) { try { metadata = JSON.parse(row.metadata); } catch { /* ignore */ } }
  return <dialog ref={dialog} className="ws-dialog au-dialog" aria-label={row.summary} onClose={onClose} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div className="ws-dialog-head"><span className="au-dialog-title"><span className={cn("pw-badge", ACTION_TONE[row.action])}>{row.action}</span><h2>{row.summary}</h2></span>
      <button className="pw-icon-button" aria-label="Close" onClick={onClose}><X size={16} /></button></div>
    <div className="ws-dialog-body">
      <dl className="au-facts">
        <div><dt>When</dt><dd>{formatTimestamp(row.ts)}</dd></div>
        <div><dt>Admin</dt><dd>{row.admin_name} <span className="ws-muted">{row.admin_discord}</span></dd></div>
        <div><dt>Target</dt><dd>{row.target_playfab ? <><Link to={playerHref(row.target_playfab)}>{row.target_label ?? "—"}</Link> <span className="ws-mono ws-muted">{row.target_playfab}</span></> : "—"}</dd></div>
        <div><dt>Changed keys</dt><dd className="ws-mono">{row.changed_keys ?? "—"}</dd></div>
      </dl>
      {!row.blob_key && metadata === null && <p className="ws-muted">No additional data captured.</p>}
      {metadata !== null && <details className="au-fold"><summary><ChevronRight size={13} />Metadata</summary><pre>{JSON.stringify(metadata, null, 2)}</pre></details>}
      {row.blob_key && <>
        {blobQuery.isLoading && <div className="pw-empty au-loading" role="status"><span className="pw-loading-dot" />Loading diff…</div>}
        {blobQuery.error && <p className="ws-alert" role="alert">Failed to load diff: {String(blobQuery.error)}</p>}
        {blobQuery.data && allKeys.length === 0 && <p className="ws-muted">Blob present but empty.</p>}
        {allKeys.map(k => <KeyDiff key={k} keyName={k} before={before[k]} after={after[k]} />)}
      </>}
    </div>
  </dialog>;
}

function KeyDiff({ keyName, before, after }: { keyName: string; before: string | undefined; after: string | undefined }) {
  const beforeText = pretty(before);
  const afterText = pretty(after);
  const noChange = beforeText === afterText;
  return <details className="au-fold au-diff" open={!noChange}>
    <summary><ChevronRight size={13} /><span className="ws-mono">{keyName}</span><span className={cn("pw-badge", !noChange && "au-badge-changed")}>{noChange ? "no change" : "changed"}</span></summary>
    <div className="au-panes"><DiffPane title="Before" value={beforeText} /><DiffPane title="After" value={afterText} /></div>
  </details>;
}

function DiffPane({ title, value }: { title: string; value: string }) {
  return <div className="au-pane">
    <div className="au-pane-title">{title}</div>
    {value ? <CodeMirror value={value} readOnly theme={oneDark} extensions={[json()]} height="22rem"
      basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: false, bracketMatching: true }}
      onCreateEditor={view => { ensureSyntaxTree(view.state, view.state.doc.length, 5000); foldAll(view); }} />
      : <p className="au-pane-empty">empty</p>}
  </div>;
}
